import { FC, useMemo, useState } from 'react'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { createAdvisorSelector, createTimeslotSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import styles from 'components/features/coaching/advisor/AdvisorCard/AdvisorCard.styl'
import { PaneNote } from 'components/features/coaching/advisorMeeting/PaneNote/PaneNote'
import { AvailabilityPicker } from 'components/features/coaching/advisorMeeting/AvailabilityPicker/AvailabilityPicker'
import { AdvisorUid, Advisor } from 'components/features/coaching/advisor/types/advisor'
import { AdvisorHeaderWith } from 'components/features/coaching/advisor/AdvisorHeaderWith/AdvisorHeaderWith'
import { TRUNCATED_BIO_LENGTH } from 'components/features/coaching/advisorMeeting/types/constants'

const [, timeZone] = new Date().toTimeString().match(/\(([^)]+)\)$/) ?? [, null]

type AdvisorCardProps = {
  uid: AdvisorUid
  onTimeslot: (timeslot: string) => void
}
export const AdvisorCard: FC<AdvisorCardProps> = (props) => {
  const advisorSelector = useMemo(createAdvisorSelector, [])
  const advisor = useStoreSelector((state) => advisorSelector(state, props))

  if (!advisor) {
    return null
  }

  return (
    <AdvisorCardInner advisor={ advisor } { ...props } />
  )
}

type AdvisorCardInnerProps =
  & AdvisorCardProps
  & {
    advisor: Advisor
  }
const AdvisorCardInner: FC<AdvisorCardInnerProps> = (props) => {
  const { advisor, onTimeslot } = props

  const timeZoneChild = useMemo(() => {
    if (!timeZone) {
      return null
    }

    return (
      <div className={ styles.timezone }>(Time Zone: { timeZone })</div>
    )
  }, [])

  const truncateStr = '...'
  const truncate = (str: string, n: number) => (str.length > n) ? str.substr(0, n) + truncateStr : str
  const [isBioExpanded, setBioExpanded] = useState(false)
  const shouldTruncate = (advisor.bio?.length - truncateStr.length) > TRUNCATED_BIO_LENGTH && !isBioExpanded
  const advisorBio = shouldTruncate ? truncate(advisor.bio, TRUNCATED_BIO_LENGTH) : advisor.bio
  const timeslotSelector = useMemo(createTimeslotSelector, [])
  const timeslots = useStoreSelector((state) => timeslotSelector(state, props))
  const ifExistsTimeslotsAvailable = Boolean(timeslots && timeslots.length)

  const expandTitleLink = useMemo(() => shouldTruncate && (
    <a onClick={ () => setBioExpanded(true) }> Read more</a>
  ), [setBioExpanded, shouldTruncate])

  return (
    <>
      <div className={ styles.advisorCard }>
        <AdvisorHeaderWith uid={ advisor.uid } showRating />
        { advisor.bio && (
          <PaneNote className={ styles.paneNote }>
            <blockquote>
              "{ advisorBio }"
              { expandTitleLink }
            </blockquote>
          </PaneNote>
        )}
      </div>
      <hr className={ styles.divider } />
      <PaneNote className={ styles.paneNote }>
        <div className={ styles.availability }>
          <h4>Availability</h4>
          { timeZoneChild }
        </div>
      </PaneNote>
      {ifExistsTimeslotsAvailable 
        ? <AvailabilityPicker uid={ advisor.uid } onSelect={ onTimeslot } /> 
        : (
          <PaneNote className={ styles.paneNoTimeslots }>
            <h4>No timeslots available at the moment</h4>
            <h6>Please check back at a later time</h6>
          </PaneNote>
        )}
    </>
  )
}
